import React, { useMemo, useEffect } from 'react'
import { Upload, Icon, Input, Switch, Button, Tooltip, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { HelpIcon } from '../../../../shared/components/HelpTooltipIcon'
import { stripScheme } from '../../../../../services/utils'
import { PREVIEW_ACCEPT, PREVIEW_MAX_SIZE_MB, isAllowedPreview } from '../../utils/fileValidation'

const { Dragger } = Upload

// Body of the "Dynamic Creative Optimization (DCO)" upload mode: an HTTPS URL
// plus a mandatory preview (image or video), instead of a media file.
//
// `value` is { url, reloadOnDisplay, whitelist, preview } and lives in the parent
// modal, which owns submit and reset. `onChange` takes a partial patch.
const DynamicAdForm = ({ value, onChange }) => {
  const { t } = useTranslation()
  const { url, reloadOnDisplay, whitelist, preview } = value

  // Single file, replaced on each pick. Image or video — see fileValidation.
  // A video preview is transcoded server-side, so the ad lands with its preview
  // still pending.
  const beforePreviewUpload = (file) => {
    if (!isAllowedPreview(file)) {
      message.error(t('The preview must be an image or video of up to {{max}}MB', { max: PREVIEW_MAX_SIZE_MB }))
      return false
    }
    onChange({ preview: file })
    return false
  }

  // Preview of the picked file, rendered inside the dropzone. Derived during
  // render, NOT held in state: state fed from an effect lags one render behind
  // `preview`, so clearing the file left a stale URL and the branch below read
  // `preview.name` off null. The effect only revokes — an object URL pins the
  // file in memory until it does.
  const previewUrl = useMemo(() => (preview ? URL.createObjectURL(preview) : null), [preview])
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const isVideoPreview = !!preview && (preview.type || '').startsWith('video/')

  const setWhitelistAt = (index, next) =>
    onChange({ whitelist: whitelist.map((item, i) => (i === index ? next : item)) })

  const removeWhitelistAt = (index) =>
    onChange({ whitelist: whitelist.filter((_, i) => i !== index) })

  return (
    <>
      <div className="field">
        <p className="field-label">{t('Ad URL')}</p>
        <Input
          addonBefore="https://"
          value={url}
          onChange={(e) => onChange({ url: stripScheme(e.target.value) })}
          placeholder="example.com/my-ad"
        />
      </div>

      <div className="field">
        <p className="field-label">
          {t('External URLs')}
          <Tooltip
            title={t(
              'Add every external service URL your HTML needs to work, such as weather or traffic APIs and other resources.',
            )}
          >
            <span className="field-help">
              <HelpIcon />
            </span>
          </Tooltip>
        </p>
        {whitelist.map((item, index) => (
          <div className="url-row" key={index}>
            <Input
              addonBefore="https://"
              value={item}
              onChange={(e) => setWhitelistAt(index, stripScheme(e.target.value))}
              placeholder="api.example.com"
            />
            <Icon
              type="close"
              className="url-remove"
              title={t('Remove')}
              onClick={() => removeWhitelistAt(index)}
            />
          </div>
        ))}
        <Button
          type="link"
          onClick={() => onChange({ whitelist: [...whitelist, ''] })}
        >
          <Icon type="plus" /> {t('Add URL')}
        </Button>
      </div>

      <div className="field">
        <p className="field-label">
          {t('Preview')}
          <Tooltip title={t('Provide an image or video of the ad in action.')}>
            <span className="field-help">
              <HelpIcon />
            </span>
          </Tooltip>
        </p>
        <Dragger accept={PREVIEW_ACCEPT} showUploadList={false} beforeUpload={beforePreviewUpload}>
          {previewUrl ? (
            <div className="preview-media">
              {/* stopPropagation: this sits inside the Dragger, whose click opens
                  the file picker. */}
              <Icon
                type="close"
                className="preview-remove"
                title={t('Remove')}
                onClick={(e) => {
                  e.stopPropagation()
                  onChange({ preview: null })
                }}
              />
              {isVideoPreview ? (
                <video src={previewUrl} muted loop autoPlay playsInline />
              ) : (
                <img src={previewUrl} alt="" />
              )}
              <p className="preview-name" title={preview.name}>
                {preview.name}
              </p>
              <p className="upload-hint">{t('Click or drag to replace')}</p>
            </div>
          ) : (
            <div className="dragger-content">
              <p className="ant-upload-drag-icon">
                <Icon type="picture" />
              </p>
              <p>{t('Click to upload or drag your files here')}</p>
              <p className="upload-hint">
                {t('Supports images and videos (Max {{max}}MB)', { max: PREVIEW_MAX_SIZE_MB })}
              </p>
            </div>
          )}
        </Dragger>
      </div>
      {/* Inline row: a boolean reads better next to its label, and a flex-column
          .field would stretch the switch across the whole modal. */}
      <div className="field field-inline">
        <p className="field-label">{t('Reload on display')}</p>
        <Switch
          size="small"
          checked={reloadOnDisplay}
          onChange={(checked) => onChange({ reloadOnDisplay: checked })}
        />
      </div>
    </>
  )
}

export default DynamicAdForm
